import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Contact,
  HandCoins,
  LayoutDashboard,
  LogOut,
  PackagePlus,
  PackageSearch,
  Receipt,
  Smartphone,
  Truck,
  Users,
  Warehouse,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";

const ALL = ["owner", "branch_manager", "admin_gudang", "admin_toko"];
const MENUS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ALL, end: true, testid: "nav-dashboard" },
  { to: "/produk", label: "Products", icon: Smartphone, roles: ALL, testid: "nav-produk" },
  { to: "/gudang", label: "Warehouses", icon: Warehouse, roles: ["owner", "branch_manager", "admin_gudang"], testid: "nav-gudang" },
  { to: "/stok-masuk", label: "Stock In", icon: PackagePlus, roles: ["owner", "branch_manager", "admin_gudang"], testid: "nav-stok-masuk" },
  { to: "/transfer", label: "Delivery Notes", icon: Truck, roles: ["owner", "branch_manager", "admin_gudang"], testid: "nav-transfer" },
  { to: "/penjualan", label: "Invoices", icon: Receipt, roles: ["owner", "branch_manager", "admin_toko"], testid: "nav-penjualan" },
  { to: "/customers", label: "Customers", icon: Contact, roles: ["owner", "branch_manager", "admin_toko"], testid: "nav-customers" },
  { to: "/piutang", label: "Receivables", icon: HandCoins, roles: ["owner", "branch_manager", "admin_toko"], testid: "nav-piutang" },
  { to: "/stok-check", label: "Stock Check", icon: PackageSearch, roles: ALL, testid: "nav-stok-check" },
  { to: "/laporan", label: "Reports", icon: BarChart3, roles: ALL, testid: "nav-laporan" },
  { to: "/pengguna", label: "Users", icon: Users, roles: ["owner"], testid: "nav-pengguna" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menus = MENUS.filter((m) => m.roles.includes(user.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-zinc-950 text-zinc-300">
        <div className="flex items-center gap-3 border-b border-zinc-800 px-6 py-5">
          <img src="/logo.png" alt="Horizon logo" className="h-9 w-9 rounded-sm object-contain p-0.5" data-testid="sidebar-logo" />
          <div>
            <p className="font-heading text-base font-bold tracking-tight text-white">Horizon</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {menus.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              data-testid={m.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <m.icon className="h-4 w-4" />
              {m.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">{user.name}</p>
              <p className="text-[11px] text-zinc-500" data-testid="sidebar-user-role">{ROLE_LABELS[user.role] || user.role}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              title="Sign out"
              className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <div className="ml-64 flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-zinc-200 bg-white/80 px-8 py-3 backdrop-blur-md">
          <p className="text-sm text-zinc-500">
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700" data-testid="header-role-badge">
            {ROLE_LABELS[user.role] || user.role}
          </span>
        </header>
        <main className="flex-1 px-8 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
